export type Source = "ga4" | "gsc";
export type Tab = "overview" | "search" | "traffic" | "pages" | "connections";
export type Compare = "previous" | "none";
export interface DateRange {
  start: string;
  end: string;
  compare: Compare;
}
export interface Website {
  id: string;
  name: string;
  scope_kind: string;
  scope_value: string | null;
  primary_ga4_property_id: string | null;
  primary_gsc_site_id: string | null;
  mapping_status: "confirmed" | "needs_review";
  mapping_version: number;
  merged_into_id: string | null;
  created_at: string;
  updated_at: string;
}
export interface WebsiteList {
  items: Website[];
  total_available_rows: number;
  page: number;
  page_size: number;
}
export interface SourceOwner {
  website_id: string;
  name: string;
  mapping_version: number;
}
export interface SourceState {
  account_email?: string | null;
  resource_scope?: string;
  resource_id?: string;
  connection_status:
    | "not_connected"
    | "connected"
    | "reauth_required"
    | "permission_denied";
  sync_status: "idle" | "queued" | "running" | "failed";
  data_status: "ready" | "empty" | "partial" | "unavailable";
  freshness: "fresh" | "stale" | "unknown";
  last_successful_sync_at: string | null;
  data_through: string | null;
  reason_code: string | null;
  reporting_timezone: string;
  available_breakdowns: string[];
}
export interface ReportContext {
  timezone_reason_code?: string | null;
  website_id: string;
  mapping_version: number;
  dataset_version: number | string;
  start_date: string;
  end_date: string;
  comparison_start: string | null;
  comparison_end: string | null;
  reporting_timezone: string;
}
export interface ContextResponse {
  context: ReportContext;
  sources: Record<Source, SourceState>;
}
export interface Envelope<T> extends ContextResponse {
  payload: T;
  generated_at: string;
}
export interface Metric {
  comparison_available?: boolean;
  value: number | null;
  previous: number | null;
  change: number | null;
  unit: "count" | "percent" | "position";
  quality: "exact" | "approximate" | "unavailable";
  provenance: string;
  reason_code: string | null;
}
export type MetricName =
  | "clicks"
  | "impressions"
  | "sessions"
  | "users"
  | "pageviews"
  | "ctr"
  | "position";
export interface Overview {
  metrics: Record<MetricName, Metric>;
  highlights: { title: string; description: string; source: string }[];
}
export interface Timeseries {
  points: { date: string; value: number | null; previous: number | null }[];
  metric: string;
  source: Source;
}
export interface TablePayload {
  items: Record<string, unknown>[];
  total_available_rows: number;
  page: number;
  page_size: number;
  truncated: boolean;
  limitations: string[];
}
export interface Insight {
  id: string;
  title: string;
  description: string;
  source: string;
  generated_at: string;
  period_start: string;
  period_end: string;
  evidence_ids: string[];
  affected_pages: string[];
  outdated: boolean;
}
export interface Insights {
  items: Insight[];
  can_explain: boolean;
  limitations: string[];
}
export interface RefreshOperation {
  start?: string;
  end?: string;
  compare?: Compare;
  id: string;
  status: "queued" | "running" | "completed" | "partial" | "failed";
  sources: Partial<Record<Source, { status: string; message: string | null }>>;
}
export const tabs: { id: Tab; label: string }[] = [
  { id: "overview", label: "Overview" },
  { id: "search", label: "Search performance" },
  { id: "traffic", label: "Traffic & engagement" },
  { id: "pages", label: "Pages" },
  { id: "connections", label: "Connections" },
];
export const sourceLabel: Record<Source, string> = {
  ga4: "Google Analytics",
  gsc: "Search Console",
};
export const metricLabels: Record<MetricName, string> = {
  clicks: "Search clicks",
  impressions: "Search impressions",
  sessions: "Sessions",
  users: "Active users",
  pageviews: "Page views",
  ctr: "Click-through rate",
  position: "Average position",
};
